import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { getPageTitle, organization } from "../../config";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import axios from "axios";

import ProjectsPublic from "./ProjectsPublic";
import EventsPublic from "./EventsPublic";
import DonatePublic from "./DonatePublic";
import RentPublic from "./RentPublic";
import ResourcesPublic from "./ResourcesPublic";
import StaffPublic from "./StaffPublic";
import CallToAction from "../../components/CallToAction";
import { SEO, getWebPageStructuredData } from "../../components/SEO";

const staticMap = {
    "events": EventsPublic,
    "staff": StaffPublic,
    "resources": ResourcesPublic,
    "donate": DonatePublic,
    "rent": RentPublic
};

// SEO descriptions for key pages (matching the existing site's Google search results)
const pageDescriptions = {
    "programs": "Neighborhood Service Center has programs in place to help the community with services including energy assistance, housing, food programs, and more.",
    "staff": "Meet the dedicated staff and board members at Neighborhood Service Center including Yvette Robinson, Ms. Kathy Taylor, Cardeaner and other team members.",
    "contact": "Visit Us. We are located at 126 Port Street Easton MD 21601. Contact the Neighborhood Service Center for assistance and services.",
    "donate": "These can be donated to NSC at 126 Port Street, Easton. Support the Neighborhood Service Center's mission to help low-income families and elderly.",
    "about": "The Neighborhood Service Center, Inc. is a non-profit Community Action Agency serving Talbot County, Maryland.",
    "events": "Upcoming events and community activities at the Neighborhood Service Center.",
    "resources": "Resources and information available through the Neighborhood Service Center to help community members.",
    "privacy": "Privacy Policy for the Neighborhood Service Center website.",
    "terms": "Terms of Service for the Neighborhood Service Center website."
};

export default function DynamicPublicPage() {
    const location = useLocation();
    const pathname = location.pathname.replace(/^\//, "");
    const [doc, setDoc] = useState(null);
    const [loading, setL] = useState(true);

    useEffect(() => {
        setL(true);
        axios.get(`/api/content/${encodeURIComponent(pathname)}`)
            .then(r => setDoc(r.data))
            .catch(() => setDoc(null))
            .finally(() => setL(false));
    }, [pathname]);

    useEffect(() => {
        if (doc) {
            document.title = getPageTitle(doc.title || pathname);
        } else {
            document.title = organization.name;
        }
    }, [doc, pathname]);

    if (loading) return <p className="text-center mt-5">Loading…</p>;
    if (!doc) return <></>;

    const backgroundUrl = doc?.image
        ? `url('${doc.image}')`
        : "url('/images/background.jpg')";

    const StaticComponent = staticMap[pathname];

    // Generate page description - use custom description if available, 
    // otherwise extract from markdown content or use default
    const getPageDescription = () => {
        // Check for page-specific predefined description
        if (pageDescriptions[pathname]) {
            return pageDescriptions[pathname];
        }
        // Extract first ~160 characters from markdown content as description
        if (doc.markdown) {
            const plainText = doc.markdown
                .replace(/#+\s/g, '') // Remove headers
                .replace(/\*+/g, '') // Remove bold/italic markers
                .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Convert links to just text
                .replace(/\n+/g, ' ') // Replace newlines with spaces
                .trim();
            return plainText.substring(0, 160) + (plainText.length > 160 ? '...' : '');
        }
        return `${doc.title} - Neighborhood Service Center`;
    };

    const pageDescription = getPageDescription();
    const structuredData = getWebPageStructuredData({
        title: doc.title,
        description: pageDescription,
        path: `/${pathname}`
    });

    return (
        <main style={{ fontFamily: "'Montserrat', sans-serif" }}>
            <SEO
                title={doc.title}
                description={pageDescription}
                path={`/${pathname}`}
                image={doc.image || undefined}
                structuredData={structuredData}
            />
            {/* Hero Section */}
            <section
                style={{
                    backgroundImage: backgroundUrl,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    position: "relative",
                    height: "320px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {/* Overlay */}
                <div
                    style={{
                        position: "absolute",
                        inset: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.4)",
                        zIndex: 1,
                    }}
                />

                {/* Title */}
                <div
                    style={{
                        position: "relative",
                        zIndex: 2,
                        textAlign: "center",
                        color: "white",
                        textShadow: "1px 1px 8px rgba(0, 0, 0, 0.8)",
                    }}
                >
                    <h1 style={{ fontSize: "2.5rem", textTransform: "", fontWeight: "700" }}>
                        {doc.title}
                    </h1>
                </div>
            </section>

            <div className="container my-5">
                {doc.use_static && StaticComponent ? (
                    <>
                        {doc.cta?.position === 'before' && <CallToAction cta={doc.cta} />}
                        <StaticComponent />
                        {doc.cta?.position === 'after' && <CallToAction cta={doc.cta} />}
                    </>
                ) : (
                    <div className="container markdown-body" style={{ maxWidth: 800 }}>
                        {doc.cta?.position === 'before' && <CallToAction cta={doc.cta} />}
                        <ReactMarkdown rehypePlugins={[rehypeRaw]}>{doc.markdown}</ReactMarkdown>
                        {doc.cta?.position === 'after' && <CallToAction cta={doc.cta} />}
                    </div>
                )}
            </div>
        </main>
    );
}
